import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ContentComponent } from './shared/components/layout/content/content.component';
import { content, agencyContent } from './shared/routes/routes/routers';
import { FullComponent } from './shared/components/layout/full/full.component';
import { full } from './shared/routes/full';
import { authGuard } from './core/auth/guards/auth.guard';
import { agencyLevelGuard } from './core/guards/agency-level.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { TermsComponent } from './pages/terms/terms.component';
import { AgreementComponent } from './pages/agreement/agreement.component';
import { PrivacyComponent } from './pages/privacy/privacy.component';
import { PermissionsResolver } from './core/permissions/resolvers/permissions.resolver';
import { InvalidLinkComponent } from './shared/components/invalid-link/invalid-link.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { DemoGuard } from './core/auth/guards/demo.guard';
import { DemoLoginComponent } from './features/auth/demo-login/demo-login.component';
import { defaultRedirectGuard } from './core/guards/default-redirect.guard';

// Note: Routing animation setting moved to LocalStorageService with LocalStorageKeys.ANIMATE
// const routingAnimation = this.localStorage.get<string>(LocalStorageKeys.ANIMATE);

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [authGuard, defaultRedirectGuard],
    component: ContentComponent
  },

  // Демо-маршрут для агенцій. Мусить йти ПЕРЕД '/demo' інакше Angular матиме
  // перший збіг із загальним '/demo/*' блоком і не знайде agency-сторінки.
  // DemoGuard замість agencyLevelGuard — agency-only gating під демо
  // забезпечується тим, що sub-кредити демо-користувача мають
  // merchant.isAgency=true і активну агенцій-підписку (див. Task 1).
  {
    path: 'demo/agency',
    component: ContentComponent,
    canActivate: [DemoGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      ...agencyContent
    ]
  },

  // Додаємо спеціальний демо-маршрут
  {
    path: 'demo',
    component: ContentComponent,
    canActivate: [DemoGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      // Копіюємо всі існуючі маршрути з content
      ...content
    ]
  },

  {
    path: 'agency',
    canActivate: [authGuard, agencyLevelGuard],
    resolve: { permissions: PermissionsResolver },
    component: ContentComponent,
    children: agencyContent
  },

  {
    path: '',
    // bd-sr1l: DemoGuard added here (was only on /demo route) so demo sessions
    // navigating to /settings, /dashboard, etc. directly via URL get redirected
    // back to /demo/<path>. Without this guard demo users could reach
    // non-demo-prefixed routes (e.g. /settings?section=shops) where the
    // template-level [disabled]="isDemoMode()" gate could be bypassed via
    // DevTools — the bd-erxr exploit chain.
    canActivate: [authGuard, DemoGuard],
    component: ContentComponent,
    resolve: { permissions: PermissionsResolver },
    children: content
  },

  {
    path: '',
    canActivate: [NoAuthGuard],
    component: FullComponent,
    children: [
      {
        path: 'login',
        loadChildren: () => import('./features/auth/login/login.routing')
      },
      {
        path: 'sign-up',
        loadChildren: () => import('./features/auth/sign-up/sign-up.routing')
      },
      {
        path: 'collaborator-sign-up',
        loadChildren: () =>
          import('./features/auth/collaborator-sign-up/collaborator-sign-up.component').then(
            m => m.COLLABORATOR_SIGN_UP_ROUTES
          )
      },
      {
        path: 'request-reset-password',
        loadChildren: () =>
          import('./features/auth/request-reset-password/request-reset-password.routing')
      },
      {
        path: 'reset-password',
        loadChildren: () => import('./features/auth/reset-password/reset-password.routing')
      }
    ]
  },

  {
    path: '',
    component: FullComponent,
    children: full
  },
  {
    path: 'demo-login',
    component: DemoLoginComponent
  },
  {
    path: 'terms',
    component: TermsComponent,
    pathMatch: 'full'
  },
  {
    path: 'agreement',
    component: AgreementComponent,
    pathMatch: 'full'
  },
  {
    path: 'privacy',
    component: PrivacyComponent,
    pathMatch: 'full'
  },
  {
    path: 'auth-success',
    loadChildren: () => import('./features/auth/auth-success/auth-success.routing')
  },
  {
    path: 'connect-shop',
    loadChildren: () =>
      import('./features/connect-shop/connect-shop.component').then(m => m.CONNECT_SHOP_ROUTES)
  },
  {
    path: 'invite-success',
    loadChildren: () =>
      import('./features/invite-success/invite-success.component').then(
        m => m.INVITE_SUCCESS_ROUTES
      )
  },
  {
    path: 'invalid-link',
    component: InvalidLinkComponent
  },
  // Public branded report viewer — no auth, no shell. Agency shares this URL
  // with their clients via the share-link modal on the internal preview.
  {
    path: 'reports/public/:token',
    loadComponent: () =>
      import(
        './features/agency-reports/components/public-report-viewer/public-report-viewer.component'
      ).then(m => m.PublicReportViewerComponent)
  },
  {
    path: '404',
    component: NotFoundComponent
  },
  {
    path: '**',
    redirectTo: '404',
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
